/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */
import {Debug, WebGLContext, ShadowVolumesGenerator, RenderDocument} from "js_renderer";
import AbstractModule from "../abstract_module";
import {vec2} from "gl-matrix";
import IShadowsModule from "./scene_shadows_interface";
import Scene from "../../scene";

export default
class ShadowsModule extends AbstractModule implements IShadowsModule {

    protected static _typeName: string = "shadows";
    private _shadowVolume: { generator: ShadowVolumesGenerator; enabled: boolean, computationCB: any; shadowVolumesEnabled: boolean };
    private _shadowMap: { debug: number; lightBleeding: number; type: string; enabled: boolean };

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._type = ShadowsModule._typeName;

        // shadow generation and enable/disable state
        this._shadowVolume = {
            enabled: false,
            generator: new ShadowVolumesGenerator(this.renderDocument),
            shadowVolumesEnabled: false,
            computationCB: null
        };

        this._shadowMap = {
            enabled: false,
            type: null,
            debug: 0,
            lightBleeding: 0.5
        };

        this.setShadowMapType('vsm');

        this.sceneAPI.getActivityModule().registerObserver(this);
    }

    public static getClassType(): string {
        return ShadowsModule._typeName;
    }

    /**
     * When manipulator matrix is updated
     */
    public onManipulatorChanged() {
        // Invalidate shadow map and it's light
        this.invalidateShadowMapAndLight();
    }

    public initShadows() {
        // For both
        // Recreate framebuffers for shadow maps
        if (this.getShadowMapEnabled()) {
            this.invalidateShadowMapFramebuffers();
        }
    }

    public getShadowSettings(): {shadowIntensity: number, sunOrientationAngle: number, sunElevationAngle: number} {
        return {
            shadowIntensity: this.sceneAPI.getLightingModule().getShadowIntensity(),
            sunOrientationAngle: this.sceneAPI.getLightingModule().getSunOrientationAngle(),
            sunElevationAngle: this.sceneAPI.getLightingModule().getSunElevationAngle()
        };
    }

    public setSunOrientation(aAngleRad: number) {
        this.sceneAPI.getLightingModule().setSunLightOrientation(aAngleRad);
    }

    public setSunElevation(aAngleRad: number) {
        this.sceneAPI.getLightingModule().setSunLightElevation(aAngleRad);
    }

    public setShadowIntensity(aShadowIntensity: number) {
        this.sceneAPI.getLightingModule().setShadowIntensity(aShadowIntensity);

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    public getShadowIntensity(): number {
        return this.sceneAPI.getLightingModule().getShadowIntensity();
    }

    public enableShadowVolumes(aShadowComputationCB: any, aValue?: boolean) {
        this._shadowVolume.enabled = (aValue === undefined) ? true : aValue;

        if (this._shadowVolume.enabled && !WebGLContext.useInstancing) {
            this._shadowVolume.enabled = false;
            Debug.logInfo("WebGL error: shadows (via the shadow volumes algorithm) cannot be computed because webgl " +
                "instancing is not supported on this device. The shadow maps algorithm will be used instead.");
            return;
        }

        this._shadowVolume.computationCB = aShadowComputationCB;

        this.renderDocument.enableShadows(this._shadowVolume.enabled);

        this.sceneAPI.getLightingModule().enableShadows(this._shadowVolume.enabled);

        // start computing the shadows with timeout just to show the computing shadows dialog in app (TODO: use worker
        // thread to compute the shadow volumes)
        let self = this;
        window.setTimeout(function() {
            // release the previous shadow volumes geometry by default
            self._shadowVolume.generator.release();

            // if the shadow volumes is enabled, just generate the shadow volumes geometry
            if (self._shadowVolume.enabled) {
                self._shadowVolume.generator.generate();
            }

            if (self._shadowVolume.computationCB) {
                self._shadowVolume.computationCB();
            }
        }, 100);
    }

    public disableShadowVolumes(aShadowComputationCB: any) {
        this.enableShadowVolumes(aShadowComputationCB, false);
    }

    public getShadowVolumesEnabled(): boolean {
        return this._shadowVolume.enabled;
    }

    public getShadowsEnabled(): boolean {
        return this.getShadowVolumesEnabled() || this.getShadowMapEnabled();
    }

    public recompute() {
        if (this.getShadowVolumesEnabled()) {
            this.enableShadowVolumes(null, false);
            this.enableShadowVolumes(null, true);
        }
    }

    public setShadowMapEnabled(aValue: boolean) {
        // If enabled or disabled, don't do anything
        if (this._shadowMap.enabled === aValue) {
            return;
        }

        this._shadowMap.enabled = aValue;
        this._shadowMap.debug = 0;

        // Recreate framebuffers
        this.invalidateShadowMapFramebuffers();

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    public setShadowMapType(aValue: string) {
        let prev = this._shadowMap.type;

        if (aValue === 'pcf') {
            this._shadowMap.type = 'pcf';
        } else if (aValue === 'vsm') {
            // Check if extension is available, if not, go back to pcf
            if (!WebGLContext.getFeatureAvailable("STD_DERIVATIVES")) {
                Debug.logInfo("Shadow map: enabling PCF because of the missing extension OES_standard_derivatives");
                this._shadowMap.type = 'pcf';
            } else {
                this._shadowMap.type = 'vsm';
            }
        } else {
            Debug.logInfo("Cannot set type of shadow maps to " + aValue);
        }

        // Recreate framebuffers
        if (prev !== null && prev !== this._shadowMap.type) {
            this.invalidateShadowMapFramebuffers();
        }
    }

    public setShadowMapDebugBuffer(aValue: number) {
        if (aValue < 0) {
            aValue = 0;
        } else if (aValue > 2) {
            aValue = 2;
        }
        this._shadowMap.debug = aValue;
    }

    public setShadowMapLightBleeding(aValue: number) {
        this._shadowMap.lightBleeding = aValue;
        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    public getShadowMapEnabled(): boolean {
        return this._shadowMap.enabled;
    }

    public getShadowMapType(): string {
        return this._shadowMap.type;
    }

    public getShadowMapDebugBuffer(): number {
        if (!this.getShadowMapEnabled()) {
            return 0;
        }
        return this._shadowMap.debug;
    }

    public getShadowMapLightBleeding(): number {
        return this._shadowMap.lightBleeding;
    }

    public getShadowMapDefaultIntensity(): number {
        return 1.0;
    }

    public getShadowMapDefaultAmbient(): number {
        return 0.5;
    }

    public getShadowMapDefaultHeadlight(): number {
        return 0.0;
    }

    public getShadowMapDefaultSunlight(): number {
        return 0.5;
    }

    /** Use this if you need to recompile shaders or recreate framebuffers */
    public invalidateShadowMapFramebuffers() {
        let webglRenderGraph = this.renderDocument.getRenderer().getWebGLRenderGraph();
        if (webglRenderGraph) {
            webglRenderGraph.getLayer('shadowMaps').invalidate();
        }
    }

    /** Use this if you need to recalculate shadow map */
    public invalidateShadowMap() {
        let webglRenderGraph = this.renderDocument.getRenderer().getWebGLRenderGraph();
        if (webglRenderGraph) {
            webglRenderGraph.getLayer('shadowMaps').invalidateTexture();
        }
    }

    /** Use this if you need to recalculate light position, orthographic projection and shadow map */
    public invalidateShadowMapAndLight() {
        let webglRenderGraph = this.renderDocument.getRenderer().getWebGLRenderGraph();
        if (webglRenderGraph) {
            webglRenderGraph.getLayer('shadowMaps').invalidateLight();
        }
    }

    viewChanged() {
        //
    }

    viewResized(aContentSize: vec2) {
        //
    }
}

Scene.registerSceneAPIModule(ShadowsModule);
